/**
 * api/settings.js
 * REST API wrapper for plugin-wide zoom settings.
 * Mirrors the pattern in api/forms.js — all operations are ID-scoped.
 * Endpoint base: /wpbean-pgs/v1/settings
 * Nonce middleware is registered in index.js before any of these are called.
 */

import apiFetch from "@wordpress/api-fetch";

const BASE = "/wpbean-pgs/v1/settings";

/**
 * Load a single settings item by ID.
 * Returns { id, title, status, config: { ... } }
 *
 * @param {string|number} id
 */
export const getSettings = (id) => apiFetch({ path: `${BASE}/${id}` });

/**
 * Create a new settings item.
 * Calls POST /settings — same contract as createForm() in forms.js.
 * Returns { id: number }
 *
 * @param {object} data  { title, status, config }
 */
export const createSettings = (data) =>
    apiFetch({ path: BASE, method: "POST", data });

/**
 * Update an existing settings item by ID.
 * Calls PUT /settings/:id — same contract as updateForm() in forms.js.
 *
 * @param {string|number} id
 * @param {object} data   Partial or full settings payload to persist.
 */
export const updateSettings = (id, data) =>
    apiFetch({ path: `${BASE}/${id}`, method: "PUT", data });

/**
 * Partial update — only sends the fields provided.
 * Uses PATCH so PHP only writes the keys present in the request.
 *
 * @param {string|number} id
 * @param {object} data   Fields to update (e.g. { status: "draft" }).
 */
export const patchSettings = (id, data) =>
    apiFetch({ path: `${BASE}/${id}`, method: "PATCH", data });

/**
 * Duplicate a settings item by ID.
 * Fetches the original, then creates a new "draft" copy titled "<title> (Copy)".
 * Returns { id: number } of the new item.
 *
 * @param {string|number} id
 */
export const duplicateSettings = async (id) => {
    const item = await getSettings(id);
    return createSettings({
        title: `${item.title} (Copy)`,
        status: "draft",
        config: item.config ?? {},
    });
};

/**
 * Move a settings item to the trash (soft delete — recoverable).
 *
 * @param {string|number} id
 */
export const trashSettings = (id) =>
    apiFetch({ path: `${BASE}/${id}/trash`, method: "POST" });
